import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "./addcontent.css";
import { fetchThemeData, processQuestions } from "../../themeSlice";
import { selectAdminToken, selectOrganizationId, selectSessionId } from "../../sessionSlice";
import { useNavigate } from "react-router-dom";
import TabMenu from "../tabmenu/TabMenu";
import QuestionsContent from "../questionscontent/QuestionsContent";
import { getAdminQustions } from "../../functions/getAdminQuestions";

const ADMIN_ADDCONTENT_TAB_KEY = "adminAddContentActiveTab";

const ICON_SIZE = [128, 128];

const ICON_KEYS = [
  { key: "energy", label: "Energy" },
  { key: "time", label: "Time" },
  { key: "heart", label: "Heart" },
  { key: "cookie", label: "Energy Bar" },
  { key: "drink", label: "Energy drink" },
  { key: "sound", label: "Sound (icon)" },
];

/** S3 field names must match `updateThemeMedia` AUDIO_FILE_KEYS + themesets. */
const SOUND_KEYS = [
  { key: "click_answer_sound", label: "Click sound" },
  { key: "default_question_sound", label: "Default question sound" },
  { key: "energy_bar_sound", label: "Energy bar sound" },
  { key: "energy_drink_sound", label: "Energy drink sound" },
];

const ALL_MEDIA_KEYS = [...ICON_KEYS, ...SOUND_KEYS];

const MAX_MP3_BYTES = 10 * 1024 * 1024;

const AddContent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector(selectOrganizationId);
  const sessionId = useSelector(selectSessionId);
  const { currentTheme, data, status } = useSelector((state) => state.theme);

  const [files, setFiles] = useState(() =>
    ALL_MEDIA_KEYS.reduce((acc, { key }) => ({ ...acc, [key]: null }), {})
  );
  const [previews, setPreviews] = useState(() =>
    ALL_MEDIA_KEYS.reduce((acc, { key }) => ({ ...acc, [key]: null }), {})
  );
  const [activeTab, setActiveTabState] = useState("addcontent");

  const setActiveTab = (id) => {
    setActiveTabState(id);
  };
  const [uploading, setUploading] = useState(false);
  const fileInputRefs = useRef({});
  const questionCountValidationSig = useRef(null);

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme }));
  }, [dispatch, currentTheme]);

  /** Show popup only when DB has fewer questions than theme "No. of questions". */
  useEffect(() => {
    if (!data || !adminToken) return;
    let cancelled = false;
    (async () => {
      try {
        const themeNameRaw = data?.themename ?? currentTheme;
        const themeName = themeNameRaw != null ? String(themeNameRaw).trim() : "";
        if (!themeName) return;
        // Ensure org/session questions are seeded from superadmin before validating count.
        await dispatch(processQuestions(themeName)).unwrap();
        if (cancelled) return;
        const result = await getAdminQustions({ currentTheme: themeName, token: adminToken });
        const list = result?.allQuestions ?? result?.questions ?? [];
        const dbCount = Array.isArray(list) ? list.length : 0;
        const themeNo = parseInt(String(data.no_of_questions ?? "10"), 10);
        const n = Number.isFinite(themeNo) && themeNo > 0 ? themeNo : 10;
        if (cancelled) return;
        if (dbCount < n) {
          const sig = `${themeName}|${n}|${dbCount}`;
          if (questionCountValidationSig.current === sig) return;
          questionCountValidationSig.current = sig;
          await Swal.fire({
            icon: "warning",
            title: "Question count mismatch",
            html: `This theme is set to use <strong>${n}</strong> question(s), but the database only has <strong>${dbCount}</strong>. Add more questions in <strong>Questions</strong> or lower <strong>No. of questions</strong> in Theme Rules.`,
          });
        }
      } catch (e) {
        console.warn("AddContent question count check:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, data, data?.themename, data?.no_of_questions, adminToken, currentTheme]);

  useEffect(() => {
    if (!data) return;
    const s3 = process.env.REACT_APP_S3_PATH || "";
    const initial = {};
    ALL_MEDIA_KEYS.forEach(({ key }) => {
      const path = data[key];
      initial[key] = path && String(path).trim() !== "" ? `${s3}${path}` : null;
    });
    setPreviews((prev) => ({ ...prev, ...initial }));
  }, [data]);

  useEffect(() => {
    return () => {
      Object.values(previews).forEach((url) => {
        if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
      });
    };
  }, []);

  const validateImage = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "png") {
      return "Only PNG images are allowed.";
    }
    if (file.type !== "image/png") {
      return "Only PNG images are allowed.";
    }
    return null;
  };

  const validateMp3 = (file) => {
    const ext = file.name.split(".").pop().toLowerCase();
    if (ext !== "mp3") {
      return "Only MP3 files are allowed.";
    }
    const t = (file.type || "").toLowerCase();
    if (
      t &&
      t !== "application/octet-stream" &&
      !t.startsWith("audio/")
    ) {
      return "Please choose a valid MP3 file.";
    }
    if (file.size > MAX_MP3_BYTES) {
      return "MP3 must be 10 MB or smaller.";
    }
    return null;
  };

  const getImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
        URL.revokeObjectURL(img.src);
      };
      img.onerror = () => reject(new Error("Invalid image"));
      img.src = URL.createObjectURL(file);
    });

  const handleIconFileChange = async (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const typeError = validateImage(file);
    if (typeError) {
      await Swal.fire({
        icon: "error",
        title: "This file cannot be used",
        text: typeError,
      });
      e.target.value = "";
      return;
    }

    try {
      const { width, height } = await getImageDimensions(file);
      if (width !== ICON_SIZE[0] || height !== ICON_SIZE[1]) {
        await Swal.fire({
          icon: "error",
          title: "Image size does not match",
          html: `Icons must be exactly <strong>${ICON_SIZE[0]}×${ICON_SIZE[1]} pixels</strong>.<br/>This image is <strong>${width}×${height}</strong>. Resize it and try again.`,
        });
        e.target.value = "";
        window.location.reload();
        return;
      }
    } catch {
      await Swal.fire({
        icon: "error",
        title: "Could not read this image",
        text: "Try another PNG file or export the image again.",
      });
      e.target.value = "";
      return;
    }

    setFiles((prev) => ({ ...prev, [key]: file }));
    setPreviews((prev) => {
      if (prev[key] && prev[key].startsWith("blob:")) URL.revokeObjectURL(prev[key]);
      return { ...prev, [key]: URL.createObjectURL(file) };
    });
    e.target.value = "";

    const label = ICON_KEYS.find((x) => x.key === key)?.label || "Icon";
    await Swal.fire({
      toast: true,
      position: "top-end",
      icon: "success",
      title: `${label} icon is ready`,
      text: "Click Upload when you are done to save.",
      showConfirmButton: false,
      timer: 2800,
      timerProgressBar: true,
    });
  };

  const handleSoundFileChange = (key, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const err = validateMp3(file);
    if (err) {
      Swal.fire("Invalid file", err, "error");
      e.target.value = "";
      return;
    }
    setFiles((prev) => ({ ...prev, [key]: file }));
    setPreviews((prev) => {
      if (prev[key] && prev[key].startsWith("blob:")) URL.revokeObjectURL(prev[key]);
      return { ...prev, [key]: URL.createObjectURL(file) };
    });
    e.target.value = "";
  };

  const handleUpload = async () => {
    const hasFile = Object.values(files).some(Boolean);
    if (!hasFile) {
      await Swal.fire("Info", "Select at least one file to upload.", "info");
      return;
    }
    if (!adminToken) {
      await Swal.fire("Error", "Session expired. Please log in again.", "error");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("themeName", currentTheme);
      formData.append("organizationId", organizationId || "admin");
      formData.append("sessionId", sessionId || "admin");
      formData.append(
        "extraData",
        JSON.stringify({
          themeName: data?.themeName || "",
          themeDescription: data?.themeDescription != null ? String(data.themeDescription) : "",
        })
      );

      ALL_MEDIA_KEYS.forEach(({ key }) => {
        if (files[key]) formData.append(key, files[key]);
      });

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/updateThemeMedia`, {
        method: "POST",
        headers: { Authorization: `Bearer ${adminToken}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || err.error || "Upload failed");
      }

      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Your icons and sounds have been saved successfully.",
      });
      window.location.reload();
    } catch (err) {
      await Swal.fire("Error", err.message || "Failed to upload.", "error");
      window.location.reload();
    } finally {
      setUploading(false);
    }
  };

  if (status === "loading" && !data) {
    return (
      <div className="addcontent-fullscreen">
        <div className="addcontent-loading">Loading...</div>
      </div>
    );
  }

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={() => navigate("/admin/rules")}>
          <i className="fa-solid fa-arrow-left"></i> Back
        </button>
      </div>

      <TabMenu activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Questions UI: question body max 500 chars; each option max 250 (see TEXT_LIMITS in QuestionsContent.jsx). */}
      {activeTab === "questions" && <QuestionsContent />}

      {activeTab === "addcontent" && (
        <div className="addcontent-fullscreen">
          <div className="addcontent-header">
            <h1 className="addcontent-title">Theme media</h1>
            <p className="addcontent-subtitle">
              <strong>Icons:</strong> 128×128 PNG only. <strong>Sounds:</strong> MP3 only, max 10 MB each.
            </p>
          </div>

          <div className="addcontent-media-layout">
            <h2 className="addcontent-col-title addcontent-col-title--icons">Icons</h2>
            <h2 className="addcontent-col-title addcontent-col-title--sounds">Sounds</h2>

            <div className="addcontent-col addcontent-col--icons">
              <div className="addcontent-grid addcontent-grid--icons">
                {ICON_KEYS.map(({ key, label }) => (
                  <div className="addcontent-icon-card" key={key}>
                    <div className="addcontent-icon-label">{label}</div>
                    <div
                      className="addcontent-icon-box"
                      onClick={() => fileInputRefs.current[key]?.click()}
                    >
                      {previews[key] ? (
                        <img src={previews[key]} alt={label} className="addcontent-icon-preview" />
                      ) : (
                        <span className="addcontent-icon-placeholder">
                          <i className="fa-solid fa-upload" /> 128×128 PNG
                        </span>
                      )}
                    </div>
                    <input
                      ref={(el) => {
                        fileInputRefs.current[key] = el;
                      }}
                      type="file"
                      accept=".png,image/png"
                      className="addcontent-file-hidden"
                      onChange={(e) => handleIconFileChange(key, e)}
                    />
                    {files[key] && <span className="addcontent-filename">{files[key].name}</span>}
                  </div>
                ))}
              </div>
            </div>

            <div className="addcontent-col addcontent-col--sounds">
              <div className="addcontent-grid addcontent-grid--sounds">
                {SOUND_KEYS.map(({ key, label }) => (
                  <div className="addcontent-icon-card addcontent-tile-sound" key={key}>
                    <div className="addcontent-icon-label">{label}</div>
                    <div
                      className="addcontent-sound-box"
                      onClick={() => fileInputRefs.current[key]?.click()}
                    >
                      {previews[key] ? (
                        <audio
                          controls
                          src={previews[key]}
                          className="addcontent-audio-preview"
                          onClick={(ev) => ev.stopPropagation()}
                        />
                      ) : (
                        <span className="addcontent-sound-placeholder">
                          <i className="fa-solid fa-file-audio" /> MP3 — max 10 MB
                        </span>
                      )}
                    </div>
                    <input
                      ref={(el) => {
                        fileInputRefs.current[key] = el;
                      }}
                      type="file"
                      accept=".mp3,audio/mpeg,audio/mp3"
                      className="addcontent-file-hidden"
                      onChange={(e) => handleSoundFileChange(key, e)}
                    />
                    {files[key] && <span className="addcontent-filename">{files[key].name}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="addcontent-actions addcontent-actions-bottom">
            <button
              type="button"
              className="addcontent-btn addcontent-btn-primary"
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? "Uploading…" : "Upload"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AddContent;
